// ─────────────────────────────────────────────────────────────────
// RENDERER DEFAULTS
// Mapeia cada "type" do schema para as props padrão do componente.
// O campo "component" é uma string — o MetricRenderer resolve o
// componente real a partir desse nome.
// ─────────────────────────────────────────────────────────────────
export const RENDERER_DEFAULTS = {
  lineChart: {
    component: "LineChartCard",
    height: "200px",
    formatValue: (value) =>
      value != null ? parseFloat(value).toFixed(2) : "0",
  },
  trafficChart: {
    component: "CardGraphComponent",
    hideProgress: true,
    formatValue: (value) => (value ? formatTraffic(value) : "0"),
  },
  progressCard: {
    component: "CardGraphComponent",
    hideProgress: false,
    formatValue: (value, unit = "") =>
      value != null ? `${parseFloat(value).toFixed(2)} ${unit}`.trim() : " - ",
  },
  textCard: {
    component: "CardDataComponent",
    formatValue: (value) => value || "N/A",
  },
  statusCard: {
    component: "CardDataComponent",
    formatValue: (value) => (Number(value) === 1 ? "Up" : "Down"),
  },
  statusChip: {
    component: "StatusComponent",
    formatValue: (value) => (Number(value) === 1 || value === true || String(value)?.toLowerCase() === "up"),
  },
  multiLineChart: {
    component: "MultiMetricGraphComponent",
    formatValue: (value) => value,
  },
};

// ─────────────────────────────────────────────────────────────────
// THRESHOLD PRESETS
// Cada preset é um array ordenado do menor para o maior limite.
// resolveColor e resolveChip iteram e retornam o primeiro match
// onde value <= threshold.max.
//
// graphColor → cor do gráfico / ícone (string de chave do tema)
// chipColor  → cor do chip de status (string de chave do tema)
// chipLabel  → texto do chip de status
// ─────────────────────────────────────────────────────────────────
export const THRESHOLD_PRESETS = {
  // Potência recebida (dBm) — só afeta chip, graphColor é sempre fixo
  rxPower: [
    {
      max: -27,
      graphColor: "blue",
      chipColor: "critical",
      chipLabel: "Crítica",
    },
    { max: -25, graphColor: "blue", chipColor: "high", chipLabel: "Atenção" },
    { max: 0, graphColor: "blue", chipColor: "success", chipLabel: "Ok" },
  ],

  // Corrente da ONU (mA)
  onuCurrent: [
    { max: 5, graphColor: "red", chipColor: null, chipLabel: "Crítico" },
    {
      max: 8,
      graphColor: "yellow",
      chipColor: null,
      chipLabel: "Abaixo do normal",
    },
    { max: 16, graphColor: "green", chipColor: null, chipLabel: "Normal" },
    {
      max: 20,
      graphColor: "orange",
      chipColor: null,
      chipLabel: "Leve sobrecarga",
    },
    {
      max: Infinity,
      graphColor: "red",
      chipColor: null,
      chipLabel: "Sobrecarga",
    },
  ],

  // Tensão da ONU (V)
  onuVoltage: [
    { max: 2.8, graphColor: "red", chipColor: null, chipLabel: "Crítica" },
    { max: 3.0, graphColor: "yellow", chipColor: null, chipLabel: "Baixa" },
    { max: 3.4, graphColor: "green", chipColor: null, chipLabel: "Normal" },
    { max: 3.5, graphColor: "orange", chipColor: null, chipLabel: "Leve alta" },
    {
      max: Infinity,
      graphColor: "red",
      chipColor: null,
      chipLabel: "Sobretensão",
    },
  ],

  // Temperatura da ONU (°C)
  onuTemperature: [
    { max: 10, graphColor: "blue", chipColor: null, chipLabel: "Frio extremo" },
    {
      max: 20,
      graphColor: "lightblue",
      chipColor: null,
      chipLabel: "Abaixo do ideal",
    },
    { max: 50, graphColor: "green", chipColor: null, chipLabel: "Normal" },
    { max: 60, graphColor: "orange", chipColor: null, chipLabel: "Alta" },
    {
      max: Infinity,
      graphColor: "red",
      chipColor: null,
      chipLabel: "Superaquecimento",
    },
  ],

  // Potência SFP (dBm)
  sfpPower: [
    { max: -39, graphColor: "red", chipColor: null, chipLabel: "Muito baixo" },
    { max: -27, graphColor: "orange", chipColor: null, chipLabel: "Atenção baixo" },
    { max: -23, graphColor: "yellow", chipColor: null, chipLabel: "Atenção" },
    { max: Infinity, graphColor: "green", chipColor: null, chipLabel: "Normal" },
  ],

  // Corrente SFP (mA)
  sfpCurrent: [
    { max: 30, graphColor: "red", chipColor: null, chipLabel: "Muito baixo" },
    { max: 50, graphColor: "yellow", chipColor: null, chipLabel: "Baixo" },
    { max: 90, graphColor: "green", chipColor: null, chipLabel: "Normal" },
    { max: 100, graphColor: "orange", chipColor: null, chipLabel: "Alto" },
    { max: Infinity, graphColor: "red", chipColor: null, chipLabel: "Muito alto" },
  ],

  // Temperatura SFP (°C)
  sfpTemperature: [
    { max: 10, graphColor: "blue", chipColor: null, chipLabel: "Muito frio" },
    { max: 20, graphColor: "lightblue", chipColor: null, chipLabel: "Frio" },
    { max: 60, graphColor: "green", chipColor: null, chipLabel: "Normal" },
    { max: 70, graphColor: "orange", chipColor: null, chipLabel: "Quente" },
    { max: Infinity, graphColor: "red", chipColor: null, chipLabel: "Muito quente" },
  ],

  // Tensão SFP (V)
  sfpVoltage: [
    { max: 2.5, graphColor: "red", chipColor: null, chipLabel: "Muito baixo" },
    { max: 3.0, graphColor: "yellow", chipColor: null, chipLabel: "Baixo" },
    { max: 5.0, graphColor: "green", chipColor: null, chipLabel: "Normal" },
    { max: 5.5, graphColor: "orange", chipColor: null, chipLabel: "Alto" },
    { max: Infinity, graphColor: "red", chipColor: null, chipLabel: "Muito alto" },
  ],

  // Perda de pacote (%)
  packetLoss: [
    { max: 0.1, graphColor: "green", chipColor: null, chipLabel: "Sem perda" },
    { max: 2, graphColor: "green", chipColor: null, chipLabel: "Ok" },
    { max: 5, graphColor: "yellow", chipColor: null, chipLabel: "Atenção" },
    { max: 10, graphColor: "orange", chipColor: null, chipLabel: "Alto" },
    { max: Infinity, graphColor: "red", chipColor: null, chipLabel: "Crítico" },
  ],

  // Latência (ms)
  latency: [
    { max: 20, graphColor: "green", chipColor: null, chipLabel: "Excelente" },
    { max: 50, graphColor: "green", chipColor: null, chipLabel: "Bom" },
    { max: 100, graphColor: "yellow", chipColor: null, chipLabel: "Médio" },
    { max: 200, graphColor: "orange", chipColor: null, chipLabel: "Alto" },
    { max: Infinity, graphColor: "red", chipColor: null, chipLabel: "Muito alto" },
  ],
};

// ─────────────────────────────────────────────────────────────────
// resolveColor
// Retorna a string de cor do gráfico/ícone para um dado valor.
// Usa thresholdPreset se definido, senão usa ui.color como fallback.
// ─────────────────────────────────────────────────────────────────
export function resolveColor(value, uiConfig = {}) {
  if (!uiConfig?.thresholdPreset) return uiConfig?.color ?? "green";

  const preset = THRESHOLD_PRESETS[uiConfig?.thresholdPreset];
  if (!preset) return uiConfig?.color ?? "green";

  const numeric = parseFloat(value);
  if (isNaN(numeric)) return uiConfig?.color ?? "green";

  const match = preset.find((t) => numeric <= t.max);
  return match?.graphColor ?? uiConfig?.color ?? "green";
}

// ─────────────────────────────────────────────────────────────────
// resolveChip
// Retorna { color, label } para o chip de status, ou null se não
// houver chip para essa métrica.
// ─────────────────────────────────────────────────────────────────
export function resolveChip(value, uiConfig = {}) {
  if (!uiConfig?.showChipStatus || !uiConfig?.thresholdPreset) return null;

  const preset = THRESHOLD_PRESETS[uiConfig?.thresholdPreset];
  if (!preset) return null;

  const numeric = parseFloat(value);
  if (isNaN(numeric)) return null;

  const match = preset.find((t) => numeric <= t.max);
  if (!match?.chipColor) return null;

  return { color: match.chipColor, label: match.chipLabel };
}

// ─────────────────────────────────────────────────────────────────
// getProgressValue
// Normaliza um valor para 0-100% dado um progressRange { min, max }.
// ─────────────────────────────────────────────────────────────────
export function getProgressValue(value, progressRange) {
  if (!progressRange) return 0;

  const { min, max } = progressRange;
  const numeric = parseFloat(value);

  if (isNaN(numeric)) return 0;
  if (numeric >= max) return 100;
  if (numeric <= min) return 0;

  return ((numeric - min) / (max - min)) * 100;
}

// ─────────────────────────────────────────────────────────────────
// formatTraffic
// Converte bytes/s em string legível (KB/s, MB/s, GB/s).
// ─────────────────────────────────────────────────────────────────
export function formatTraffic(valueInBytesPerSecond) {
  const KB = 1000;
  const MB = KB * 1000;
  const GB = MB * 1000;

  const value = Number(valueInBytesPerSecond);

  if (value < MB) return `${(value / KB).toFixed(1)} KB/s`;
  if (value < GB) return `${(value / MB).toFixed(2)} MB/s`;
  return `${(value / GB).toFixed(2)} GB/s`;
}

// ─────────────────────────────────────────────────────────────────
// formatPing
// Formata o valor de latência em milissegundos.
// ─────────────────────────────────────────────────────────────────
export function formatPing(value) {
  const numeric = parseFloat(value);
  if (isNaN(numeric)) return "0 ms";
  return `${numeric.toFixed(1)} ms`;
}

// ─────────────────────────────────────────────────────────────────
// formatPercent
// Formata o valor em porcentagem.
// ─────────────────────────────────────────────────────────────────
export function formatPercent(value) {
  const numeric = parseFloat(value);
  if (isNaN(numeric)) return "0%";
  return `${numeric.toFixed(1)}%`;
}

// ─────────────────────────────────────────────────────────────────
// formatSysUpTime
// Formata o Uptime em dias, horas, minutos.
// ─────────────────────────────────────────────────────────────────
export function formatSysUpTime(value) {
  let seconds = parseInt(value, 10);
  if (isNaN(seconds) || seconds <= 0) return "0s";
  
  const days = Math.floor(seconds / (3600 * 24));
  seconds %= 3600 * 24;
  const hours = Math.floor(seconds / 3600);
  seconds %= 3600;
  const minutes = Math.floor(seconds / 60);
  
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  
  return parts.join(" ") || "0m";
}

// ─────────────────────────────────────────────────────────────────
// formatHaMode
// Traduz o código do Modo de Operação HA do FortiGate (fgHaSystemMode)
// para um rótulo legível. 1=standalone, 2=activeActive, 3=activePassive.
// ─────────────────────────────────────────────────────────────────
export function formatHaMode(value) {
  const map = {
    1: "Standalone",
    2: "Ativo-Ativo (A-A)",
    3: "Ativo-Passivo (A-P)",
  };
  return map[Number(value)] ?? (value ?? " - ");
}

// ─────────────────────────────────────────────────────────────────
// formatBps
// Formata uma taxa em bits/s para bps/Kbps/Mbps/Gbps (base 1000).
// Ex.: 787314000 → "787.31 Mbps"; 33500 → "33.50 Kbps".
// ─────────────────────────────────────────────────────────────────
export function formatBps(value) {
  const v = Number(value);
  if (isNaN(v)) return "0 bps";
  const K = 1000;
  const M = K * 1000;
  const G = M * 1000;
  if (v < K) return `${v.toFixed(0)} bps`;
  if (v < M) return `${(v / K).toFixed(2)} Kbps`;
  if (v < G) return `${(v / M).toFixed(2)} Mbps`;
  return `${(v / G).toFixed(2)} Gbps`;
}

// ─────────────────────────────────────────────────────────────────
// formatCount
// Formata uma contagem inteira com separador de milhar (pt-BR).
// Ex.: 159563.1 → "159.563".
// ─────────────────────────────────────────────────────────────────
export function formatCount(value) {
  const v = Number(value);
  if (isNaN(v)) return "0";
  return Math.round(v).toLocaleString("pt-BR");
}

// ─────────────────────────────────────────────────────────────────
// formatHaSync
// Estado de sincronismo HA: 1 (in sync) / 0 (out of sync). O valor pode vir
// fracionário por causa do agrupamento (ex.: 0.9999 ou média 0.5), então
// usa-se limiar >= 0.5 para "Sincronizado".
// ─────────────────────────────────────────────────────────────────
export function formatHaSync(value) {
  const v = Number(value);
  if (isNaN(v)) return "N/A";
  return v >= 0.5 ? "Sincronizado" : "Fora de sincronia";
}

// ─────────────────────────────────────────────────────────────────
// FORMATTERS MAP
// Permite que o schema escolha uma formatação específica.
// ─────────────────────────────────────────────────────────────────
export const FORMATTERS = {
  ping: formatPing,
  percent: formatPercent,
  uptime: formatSysUpTime,
  traffic: formatTraffic,
  haMode: formatHaMode,
  bps: formatBps,
  count: formatCount,
  haSync: formatHaSync,
};

// Re-exporta defaults por chave para uso no GenericDashboard
export { METRIC_KEY_DEFAULTS } from "./keyDefaults";
